import { mapYupErrorsToErrorMap, useNotifier } from "@repo/shared/common";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	createNewProjectSchema,
	createProjectPositionSchema,
	createProjectSchema,
	createProjectTeamMemberSchema,
} from "../types/ProjectValidation";

import type {
	NewProject,
	Project,
	ProjectPosition,
	ProjectTeamMember,
} from "../types/ProjectTypes";
import type { ErrorMap } from "@repo/shared/common";
import type { TFunction } from "next-i18next";
import type * as Yup from "yup";

export type ValidatorOptions<TIn> = {
	createSchema: (t: TFunction) => Yup.ObjectSchema<NonNullable<TIn>>;
};

export function useValidator<TIn extends object>(options: ValidatorOptions<TIn>) {
	const { t } = useTranslation();
	const [errors, setErrors] = useState<ErrorMap>({});
	const { notifyFailure } = useNotifier();

	// Memoize schema to avoid recreating it on every render
	// Extract createSchema from options to avoid object reference instability
	const { createSchema } = options;
	const schema = useMemo(() => createSchema(t), [createSchema, t]);

	// Memoize validate function to maintain stable reference
	const validate = useCallback(
		async (dataModel: NonNullable<TIn>): Promise<{ isValid: boolean; errors: ErrorMap }> => {
			try {
				await schema.validate(dataModel, { abortEarly: false });

				// No errors from schema validation
				const emptyErrors = {};
				setErrors(emptyErrors);
				return { isValid: true, errors: emptyErrors };
			} catch (err) {
				console.error("Validation error:", err);
				notifyFailure(t("validation-failed-plz-resolve-errors"));

				const errorMap = mapYupErrorsToErrorMap(err);
				setErrors(errorMap);
				return { isValid: false, errors: errorMap };
			}
		},
		[schema, notifyFailure, t],
	);

	return { errors, setErrors, validate };
}

// Pre-create stable options objects to avoid recreating on every render
const projectValidatorOptions = {
	createSchema: createProjectSchema,
};

const newProjectValidatorOptions = {
	createSchema: createNewProjectSchema,
};

const projectPositionValidatorOptions = {
	createSchema: createProjectPositionSchema,
};

const projectTeamMemberValidatorOptions = {
	createSchema: createProjectTeamMemberSchema,
};

export function useProjectValidator() {
	return useValidator<Project>(projectValidatorOptions);
}

export function useNewProjectValidator() {
	return useValidator<NewProject>(newProjectValidatorOptions);
}

export function useProjectPositionValidator() {
	return useValidator<ProjectPosition>(projectPositionValidatorOptions);
}

export function useProjectTeamMemberValidator() {
	return useValidator<ProjectTeamMember>(projectTeamMemberValidatorOptions);
}
